import { styled } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SideMenuLogo from './SideMenuLogo';
import MenuContent from './MenuContent';
import OptionsMenu from './OptionsMenu';
import { useAuth } from '../auth/AuthContext';
import { getNameInitials, getPrimaryDisplayRole } from '../auth/authUtils';

const drawerWidth = 232;

const Drawer = styled(MuiDrawer)({
  width: drawerWidth,
  flexShrink: 0,
  boxSizing: 'border-box',
  mt: 10,
  [`& .${drawerClasses.paper}`]: {
    width: drawerWidth,
    boxSizing: 'border-box',
  },
});

interface SideMenuProps {
  collapsed: boolean;
  onExpand: () => void;
  menuRef?: React.Ref<HTMLDivElement>;
}

export default function SideMenu({ collapsed, onExpand, menuRef }: SideMenuProps) {
  const { user, realmRoles } = useAuth();
  const displayName = user?.name ?? user?.email ?? 'User';
  const displayRole = getPrimaryDisplayRole(realmRoles);
  const email =
    user?.email && user.name !== user.email ? user.email : undefined;
  const initials = getNameInitials(displayName);

  return (
    <Drawer
      ref={menuRef}
      onMouseEnter={onExpand}
      variant="permanent"
      sx={{
        display: { xs: 'none', md: 'block' },
        [`& .${drawerClasses.paper}`]: {
          background: 'linear-gradient(180deg, #5136F2 0%, #100C2E 100%)',
          color: '#F5F5FA',
          border: 'none',
          width: collapsed ? 72 : drawerWidth,
        },
        width: collapsed ? 72 : drawerWidth,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          mt: 'calc(var(--template-frame-height, 0px) + 4px)',
          p: 1.5,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <SideMenuLogo collapsed={collapsed} />
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <Box
        sx={{
          overflow: 'auto',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <MenuContent collapsed={collapsed} />
      </Box>
      <Stack
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'rgba(255,255,255,0.1)',
          ...(collapsed
            ? {
                alignItems: 'center',
              }
            : {
                gap: 1,
                width: '100%',
                boxSizing: 'border-box',
              }),
        }}
      >
        {collapsed ? (
          <Avatar
            alt={displayName}
            sx={{
              width: 32,
              height: 32,
              fontSize: '0.75rem',
              fontWeight: 600,
              bgcolor: 'rgba(255,255,255,0.12)',
              color: '#F5F5FA',
            }}
          >
            {initials}
          </Avatar>
        ) : (
          <>
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={1.25}
              sx={{ width: '100%', minWidth: 0 }}
            >
              <Avatar
                alt={displayName}
                sx={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  bgcolor: 'rgba(255,255,255,0.12)',
                  color: '#F5F5FA',
                }}
              >
                {initials}
              </Avatar>
              <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  component="div"
                  title={displayName}
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.35,
                    color: '#F5F5FA',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {displayName}
                </Typography>
                {displayRole && (
                  <Chip
                    size="small"
                    label={displayRole}
                    variant="outlined"
                    sx={{
                      alignSelf: 'flex-start',
                      height: 22,
                      maxWidth: '100%',
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'rgba(255,255,255,0.75)',
                      '& .MuiChip-label': {
                        px: 0.75,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  />
                )}
              </Stack>
              <Box sx={{ flexShrink: 0, alignSelf: 'center', mt: 0, mr: -0.25 }}>
                <OptionsMenu />
              </Box>
            </Stack>

            {email && (
              <>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mt: 0.25, mb: 0.5 }} />
                <Box sx={{ width: '100%' }}>
                  <Typography
                    variant="overline"
                    component="div"
                    sx={{
                      display: 'block',
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      letterSpacing: '0.12em',
                      lineHeight: 1.25,
                      mb: 0.2,
                      textTransform: 'uppercase',
                    }}
                  >
                    Email
                  </Typography>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      lineHeight: 1.35,
                      color: 'rgba(255,255,255,0.85)',
                      fontWeight: 400,
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {email}
                  </Typography>
                </Box>
              </>
            )}
          </>
        )}
      </Stack>
    </Drawer>
  );
}
